import { useState, useEffect } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { CheckIcon, UnstyledButton } from '@mantine/core'
import { Flex } from '../flex'
import { Label } from '../label'
import { Text } from '../text'
import { Spacer } from '../spacer'
import { Icon } from '../icon'
import { Typography } from '../typography'
import { CopyableValueProps } from './copyable-value.types'

const ValueContainer = styled(Flex)`
  ${({ theme }) => `
    justify-content: space-between;
    align-items: center;
    background-color: ${theme.colors.gray80};
    padding: ${theme.sizes.xs} ${theme.sizes.xs} ${theme.sizes.xs} ${theme.sizes[3]};
    border-radius: ${theme.borderRadii.medium};
    user-select: all;
    position: relative;
  `}
`

const moveUpAndFade = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  70% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-10px);
  }
`

const CopyHint = styled(Flex)`
  ${({ theme }) => css`
    animation: ${moveUpAndFade} 1.5s ease-in-out;
    animation-fill-mode: forwards;
    position: absolute;
    top: -40px;
    right: 0;
    font-weight: bold;
    color: ${theme.colors.white};
    user-select: none;
    background-color: ${theme.colors.gray700};
    padding: ${theme.sizes.xs} ${theme.sizes[2]};
    border-radius: ${theme.sizes.xs};
    font-size: ${theme.fontSizes.bodySmall};
    align-items: center;
    gap: ${theme.sizes.xs};
  `}
`

export function CopyableValue({
  label,
  value,
  description,
  onCopy,
  copiedMessage = 'Copied',
  testId = 'copyable-value',
}: Readonly<CopyableValueProps>) {
  const [clipboardIsCopied, setClipboardIsCopied] = useState(false)

  useEffect(() => {
    if (clipboardIsCopied) {
      setTimeout(() => {
        setClipboardIsCopied(false)
      }, 1700)
    }
  }, [clipboardIsCopied])

  const handleCopyValue = () => {
    if (value) {
      navigator.clipboard.writeText(value)
      setClipboardIsCopied(true)
      onCopy?.()
    }
  }

  return (
    <>
      <Label htmlFor={undefined}>{label}</Label>
      <Spacer axis="vertical" size={2} />
      <ValueContainer data-testid={`${testId}-value`}>
        {clipboardIsCopied && (
          <CopyHint data-testid={`${testId}-copy-hint`}>
            <CheckIcon size={14} color="white" /> {copiedMessage}
          </CopyHint>
        )}
        <Text>{value}</Text>
        <UnstyledButton
          onClick={handleCopyValue}
          data-testid={`${testId}-copy-btn`}
        >
          <Icon name="copy" size={5} color="gray600" />
        </UnstyledButton>
      </ValueContainer>
      {description && (
        <>
          <Spacer axis="vertical" size={2} />
          <Typography variant="small" $color="gray400">
            {description}
          </Typography>
        </>
      )}
    </>
  )
}
